import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgentMailPreviewComponent } from './agent-mail-preview.component';
import {
  ApprovalAction, ApprovalRecipient, FAMILY_LABELS, approverLine, headerLine, isFinal, inProgress, outcomeText, reversibleLabel, statusLabel, statusTone,
} from './agent-action-card';

// The action card (03-APPROBATION §6, 06-FRONT §5.1): one card for the approval page (full), the « À valider » box and the
// Agent IA panel (compact). It shows what the server rendered, in this order: header, title, effects and IRRÉVERSIBLE,
// alerts (never folded), recipients with their frozen addresses, the exact preview per recipient, facts, deletions,
// the note of Claude (under the facts), the result; the gestures are given by the host page (content [cardGestures]).
@Component({
  selector: 'app-agent-action-card',
  standalone: true,
  imports: [CommonModule, AgentMailPreviewComponent],
  templateUrl: './agent-action-card.component.html',
  styleUrls: ['./agent-action-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentActionCardComponent implements OnChanges {
  @Input({ required: true }) action!: ApprovalAction;
  @Input() compact = false;
  @Input() now: Date = new Date();
  selected = 0;

  readonly familyLabels = FAMILY_LABELS;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['action'] && this.selected >= (this.action?.recipients.length || 0)) this.selected = 0;
  }

  get headingId() { return `agent-action-${this.action.id || 'carte'}`; }
  get header() { return headerLine(this.action, this.now); }
  get approver() { return approverLine(this.action); }
  get status() { return statusLabel(this.action); }
  get tone() { return statusTone(this.action.status); }
  get reversible() { return reversibleLabel(this.action); }
  get outcome() { return isFinal(this.action.status) || inProgress(this.action.status) ? outcomeText(this.action) : ''; }
  get showExecutions() { return !this.compact && Boolean(this.action.executions?.items.length) && this.action.status !== 'pending'; }
  get recipient(): ApprovalRecipient | null { return this.action.recipients[this.selected] || null; }
  get typeLabel() { return this.action.family ? this.familyLabels[this.action.family] : 'Proposition'; }

  select(index: number) { this.selected = index; }
  // Arrow keys move between the preview tabs (WAI-ARIA tabs pattern).
  tabKey(event: KeyboardEvent, index: number) {
    const total = this.action.recipients.length;
    if (!total || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? total - 1 : (index + (event.key === 'ArrowRight' ? 1 : total - 1)) % total;
    this.selected = next;
    const tabs = (event.currentTarget as HTMLElement | null)?.parentElement?.querySelectorAll<HTMLElement>('[role="tab"]');
    tabs?.[next]?.focus();
  }
  trackIndex(index: number) { return index; }
  trackKey(_: number, item: ApprovalRecipient) { return item.key; }
}
